import React, { useState, useEffect, useCallback, useRef } from 'react';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import { dateFr } from '../services/dates';
import StageModaleExclusion, { LIBELLE_MOTIF } from './StageModaleExclusion';
import StageBarreProgression from './StageBarreProgression';
import PhotoEtudiant from './PhotoEtudiant';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StagePanneauDossier from './StagePanneauDossier';
import StageModaleEnvoi from './StageModaleEnvoi';
import StageChoixTuteur from './StageChoixTuteur';

/**
 * SUIVI DE STAGE — Tableau de bord stagiaire (lot 3.1 lecture, lot 3.2 envois ①)
 * ==============================================================================
 * Spec     : doc/modules/suivi_de_stage.md §1
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#tab-dashboard)
 *
 * Le lot 3.1 a livré la LECTURE : le pipeline des onze indicateurs, la barre de
 * filtres, le tableau et le panneau latéral. Le lot 3.2 arme les DEUX envois de
 * l'étape ① — le lien commun à toute la promotion, et le lien personnel d'un
 * étudiant. Les autres actions (valider, exclure, affecter) arrivent aux lots
 * 3.3 et 3.5 ; leurs boutons sont présents mais **désactivés, avec leur lot
 * nommé dans l'infobulle**. Un bouton qui ne fait rien sans le dire se lit
 * comme une panne.
 *
 * ── LES QUATRE CASES DE VISIBILITÉ SONT MÉMORISÉES ─────────────────────────
 * ⚠️ En `localStorage`, avec une clé **par utilisateur**. La spec invoque une
 * table `utilisateurs_preferences` qui **n'existe pas** — mesuré le 2026-09-13,
 * et aucun module du projet n'en a. La convention maison est le stockage local
 * par compte (module Étudiants, séparateur de l'onglet 1). Un magasin de
 * préférences côté serveur est un sujet transverse, pas un sujet Stage.
 *
 * ── POURQUOI LA RECHERCHE EST TEMPORISÉE ───────────────────────────────────
 * Une requête par caractère en ferait huit pour « ABDELHAMID », et la réponse de
 * la troisième pourrait arriver après celle de la cinquième — la liste
 * afficherait alors le résultat d'une recherche que l'utilisateur a déjà quittée.
 */

/**
 * Le lot qui livrera chaque envoi, pour le dire dans l'infobulle du bouton
 * grisé. ⚠️ Mis à jour à l'ouverture de chaque lot : une infobulle qui annonce
 * un lot déjà livré est pire que pas d'infobulle du tout.
 */
const LOT_DU_MAIL = {
    // Lot 3.5a-3 : le courriel au tuteur est livré (armé ci-dessous).
};

/**
 * Les envois ARMÉS aujourd'hui, et la fonction d'appel de chacun.
 *
 * ⚠️ C'est une LISTE, pas une suite de `if`. Au lot 3.2 il n'y en avait qu'un et
 * le `if` suffisait ; au 3.2 bis il y en a deux, aux lots suivants il y en aura
 * cinq. Chaque envoi ajouté par un `else if` de plus, c'est une branche de plus
 * à relire — alors qu'ils font tous exactement la même chose.
 *
 * ⚠️ Un type de courriel absent d'ici ET de `LOT_DU_MAIL` afficherait un bouton
 * grisé sans explication : les deux tables se complètent, aucune ne se double.
 */
const ENVOIS_ARMES = {
    etudiant_lien: (api, dossierId, departementId) => api.preparerRelanceEtudiant(dossierId, departementId),
    maitre_fiche:  (api, dossierId, departementId) => api.preparerEnvoiMaitre(dossierId, departementId),
    // Lot 3.4 bis : la convention + le lien de dépôt, et la relance d'une correction.
    convention_depot: (api, dossierId, departementId) => api.preparerEnvoiConvention(dossierId, departementId),
    depot_a_corriger: (api, dossierId, departementId) => api.preparerRelanceCorrection(dossierId, departementId),
    // Lot 3.6 : la grille d'évaluation au maître de stage — et sa relance, même lien.
    grille_eval: (api, dossierId, departementId) => api.preparerEnvoiGrille(dossierId, departementId),
    // Lot 3.5a-3 : relancer le tuteur — le message ne cite que cet étudiant, le serveur retrouve son tuteur.
    tuteur_affectation: (api, dossierId, departementId) =>
        api.preparerEnvoiTuteurs(departementId, undefined, { genre: 'tuteur', cibles: [{ type: 'stagiaire', dossier_id: dossierId }] })
};

const DELAI_RECHERCHE_MS = 250;

/**
 * Ce que dit le bouton d'envoi d'une ligne avant qu'on clique.
 *
 * ⚠️ IL NOMME LE DESTINATAIRE, et ce n'est pas un détail : à l'étape ③ le
 * message ne part PAS à l'étudiant mais à une entreprise. L'assistante doit le
 * savoir **avant** d'ouvrir sa messagerie, pas en lisant le champ « À ».
 *
 * ⚠️ Et l'infobulle dit ce que le clic FAIT (« ouvre votre messagerie »), comme
 * la maquette : en `mailto:`, l'application ne fait qu'ouvrir une fenêtre — un
 * bouton qui laisserait croire qu'il envoie mentirait sur son propre effet.
 */
function infobulleEnvoi(d) {
    const qui = `${d.prenom} ${d.nom}`;
    if (d.action_suivante?.mail === 'maitre_fiche') {
        const base = d.action_suivante.relance
            ? `Relancer le maître de stage de ${qui}`
            : `Envoyer la fiche de ${qui} à son maître de stage`;
        return `${base} — ouvre la fenêtre d'envoi (objet et corps modifiables dans Paramétrage → Courriels)`;
    }
    if (d.action_suivante?.mail === 'grille_eval') {
        const base = d.action_suivante.relance
            ? `Relancer le maître de stage de ${qui} pour sa grille d'évaluation`
            : `Envoyer la grille d'évaluation de ${qui} à son maître de stage`;
        return `${base} — ouvre la fenêtre d'envoi (objet et corps modifiables dans Paramétrage → Courriels)`;
    }
    if (d.action_suivante?.mail === 'tuteur_affectation') {
        return `Relancer le tuteur de ${qui} (${d.tuteur ? d.tuteur.nom + ' ' + (d.tuteur.prenom || '') : ''}) : le message rappelle le lien de la fiche de visite (modèle dans Tuteurs → Paramétrage)`;
    }
    return d.action_suivante?.relance
        ? `Relancer ${qui} : son lien personnel sera préparé dans la fenêtre d'envoi`
        : `Préparer le lien personnel de ${qui}`;
}

const cleVisibilite = (user) => `stage.dashboard.visibilite.${user?.id || 'anonyme'}`;

const TONS = {
    orange: { fond: '#fff7ed', bord: '#fed7aa', texte: '#d97706' },
    bleu:   { fond: '#eff6ff', bord: '#bfdbfe', texte: '#2563eb' },
    vert:   { fond: '#f0fdf4', bord: '#bbf7d0', texte: '#16a34a' }
};

const dateCourte = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

/** « 28/02/2027 » depuis `YYYY-MM-DD` — sans passer par un fuseau (E78). */

const FILTRES_VIDES = { configurationId: '', sousNiveauId: '', semestreId: '', statut: '', tuteurId: '', recherche: '', aExclure: false };

/**
 * Lot L1a (E169) : `processus` = la vignette ouverte ({ id }). Le tableau est alors LIMITÉ à ce processus : les
 * filtres Formation et Semestre ne s'affichent pas — et l'envoi à la promotion, qui ne vise qu'UNE configuration, est
 * toujours possible. Sans `processus` : la vue d'ensemble.
 * L3a (E171) : le processus se désigne par son IDENTIFIANT (`configurationId`), plus par formation × semestre — un
 * processus peut suivre plusieurs formations.
 */
/**
 * Lot L1b : `vueEnsemble` (onglet « Tableau de bord des stages ») — UN filtre « Processus » (`processusListe`, les
 * vignettes de l'accueil) remplace Formation + Semestre ; la formation d'une ligne ouvre son processus
 * (`onOuvrirProcessus`) ; l'envoi à une promotion ne se fait pas ici (il se fait dans la vignette).
 */
function StageDashboard({ config, user, anneeId, rechercheInitiale = '', onVoirEvaluation = null, processus = null, dossierInitial = null,
                          vueEnsemble = false, processusListe = [], onOuvrirProcessus = null }) {
    const departementId = departementCourant(user, config);
    /**
     * ⚠️ LE DROIT D'ÉCRIRE, LU AVANT D'AFFICHER UN BOUTON.
     *
     * Défaut trouvé par Bertrand le 2026-09-25 : connecté avec un compte
     * « Enseignant » — qui est en **consultation** sur ce module — il voyait les
     * boutons d'action et « ça ne marchait pas ». Et pour cause : le serveur
     * répondait 403, correctement. C'est l'ÉCRAN qui mentait en proposant des
     * gestes impossibles. Le paramétrage du module le faisait déjà (lot 3.0) ;
     * le tableau de bord, non.
     */
    const modifiable = peutModifier(user, departementId, 'stage');

    const [donnees, setDonnees]       = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur]         = useState(null);
    // Lot C.1 (CB3) : ouvert depuis la Certification, la recherche arrive PRÉ-REMPLIE.
    const [filtres, setFiltres]       = useState(() => ({
        ...FILTRES_VIDES, recherche: rechercheInitiale,
        ...(processus ? { configurationId: String(processus.id) } : {})
    }));
    const [recherche, setRecherche]   = useState(rechercheInitiale);
    // E173 : la cloche ouvre le tableau de bord avec le volet du dossier, positionné sur la décision.
    const [dossierOuvert, setDossierOuvert] = useState(dossierInitial);
    /**
     * La ligne dont on modifie le suivi depuis le TABLEAU.
     *
     * ⚠️ Depuis le tableau et depuis le dossier, c'est la MÊME modale. Deux
     * écrans pour le même geste finissent par ne plus dire la même chose — et
     * c'est celui qu'on ouvre le moins qui se met à mentir.
     */
    const [exclusionLigne, setExclusionLigne] = useState(null);
    /**
     * Sur quelle section ouvrir le panneau.
     *
     * ⚠️ Remise à zéro dès que le panneau se ferme : sans ça, le dossier suivant
     * qu'on ouvrirait par un clic sur la LIGNE s'ouvrirait aussi sur la décision
     * — une position qu'on n'a pas demandée, et qu'on met un moment à comprendre.
     */
    const [ancre, setAncre] = useState(dossierInitial ? 'decision' : null);
    // Le message préparé, affiché dans sa modale (lot 3.2).
    const [luLe, setLuLe] = useState(null);
    const [envoi, setEnvoi] = useState(null);
    // Lot MC (2026-10-28) : d'où vient l'envoi ouvert, pour le REFAIRE après « Modèles de courriel… » (null : le panneau)
    const origineEnvoi = useRef(null);
    // Lot 3.S-b2 : incrémenté après un envoi par le serveur — le panneau ouvert se relit.
    const [versionPanneau, setVersionPanneau] = useState(0);
    const [enCoursEnvoi, setEnCoursEnvoi] = useState(false);

    const [visibilite, setVisibilite] = useState(() => {
        try {
            const brut = localStorage.getItem(cleVisibilite(user));
            if (brut) {
                const lu = JSON.parse(brut);
                if (Array.isArray(lu)) return lu;
            }
        } catch (_) { /* stockage bloqué : on retombe sur le défaut */ }
        return ['actif'];
    });

    // La frappe devient une requête après un silence, pas à chaque caractère.
    const minuteur = useRef(null);
    useEffect(() => {
        if (minuteur.current) clearTimeout(minuteur.current);
        minuteur.current = setTimeout(() => {
            setFiltres((f) => ({ ...f, recherche }));
        }, DELAI_RECHERCHE_MS);
        return () => { if (minuteur.current) clearTimeout(minuteur.current); };
    }, [recherche]);

    /**
     * ⚠️ AUCUN RAPPEL DE PARENT dans ces dépendances. C'est le défaut le plus
     * grave du lot 1.3 bis d'Étudiants : un rappel de parent dans les
     * dépendances d'un `useCallback` qui recharge change d'identité à chaque
     * rendu du parent, relance le chargement au milieu d'une frappe, et EFFACE
     * la saisie en cours.
     */
    /**
     * `silencieux` : relire SANS afficher « Lecture… » ni vider l'écran en cas
     * d'échec. ⚠️ Une relecture de fond qui fait clignoter l'en-tête toutes les
     * minutes, ou qui efface le tableau parce que le réseau a hoqueté une fois,
     * est pire que pas de relecture du tout.
     */
    const recharger = useCallback(async (silencieux = false) => {
        if (!departementId) return;
        if (!silencieux) setChargement(true);
        setErreur(null);
        try {
            const r = await stageAPI.getDashboard(departementId, anneeId, { ...filtres, visibilite });
            setDonnees(r.data);
            setLuLe(new Date());
        } catch (e) {
            if (silencieux) return;
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message
                || 'Lecture du tableau de bord impossible');
            setDonnees(null);
        } finally {
            if (!silencieux) setChargement(false);
        }
    }, [departementId, anneeId, filtres, visibilite]);

    useEffect(() => { recharger(); }, [recharger]);

    /**
     * ⚠️ LE TABLEAU NE PEUT PAS DEVINER CE QUI SE PASSE HORS DE L'APPLICATION.
     *
     * Un étudiant qui soumet sa fiche sur la page publique ne prévient personne :
     * l'écran restait donc figé, et il fallait le quitter puis y revenir pour
     * voir la progression bouger. Défaut trouvé par Bertrand à la recette du
     * lot 3.2, sur le premier dossier soumis pour de vrai.
     *
     * Deux relectures, toutes deux silencieuses :
     *   · au retour sur l'onglet — le geste naturel, on remplit ailleurs et on revient ;
     *   · toutes les 60 s tant que l'onglet est VISIBLE — pour l'assistante qui
     *     laisse l'écran ouvert et attend les réponses.
     *
     * ⚠️ Rien ne tourne quand l'onglet est caché : un écran en arrière-plan qui
     * interroge le serveur toute la nuit, c'est du bruit et des requêtes pour rien.
     * ⚠️ `focus` et `visibilitychange` se déclenchent souvent ENSEMBLE au retour
     * sur l'onglet : sans le garde-fou des 3 secondes, on relirait deux fois.
     */
    const rechargerRef = useRef(recharger);
    useEffect(() => { rechargerRef.current = recharger; }, [recharger]);

    useEffect(() => {
        let derniere = 0;
        const relire = () => {
            if (document.visibilityState !== 'visible') return;
            const t = Date.now();
            if (t - derniere < 3000) return;
            derniere = t;
            rechargerRef.current(true);
        };
        document.addEventListener('visibilitychange', relire);
        window.addEventListener('focus', relire);
        const minuterie = setInterval(relire, 60000);
        return () => {
            document.removeEventListener('visibilitychange', relire);
            window.removeEventListener('focus', relire);
            clearInterval(minuterie);
        };
    }, []);

    const basculerVisibilite = useCallback((cle) => {
        setVisibilite((v) => {
            const suivant = v.includes(cle) ? v.filter((x) => x !== cle) : [...v, cle];
            try { localStorage.setItem(cleVisibilite(user), JSON.stringify(suivant)); } catch (_) { /* sans effet */ }
            return suivant;
        });
    }, [user]);

    const poser = (champ, valeur) => setFiltres((f) => ({ ...f, [champ]: valeur }));

    // ── LES ENVOIS DE L'ÉTAPE ① (lot 3.2) ───────────────────────────────────

    /**
     * La configuration visée par l'envoi groupé.
     *
     * ⚠️ Il n'y en a une que si TOUS les dossiers affichés partagent la même :
     * un « envoi à la promotion » sur deux promotions mélangées enverrait le lien
     * de l'une aux étudiants de l'autre. Plutôt que de choisir à sa place, le
     * bouton reste grisé et son infobulle dit quoi filtrer.
     */
    const configurationVisee = (() => {
        const ids = new Set((donnees?.dossiers || []).map((d) => d.configuration_id));
        return ids.size === 1 ? [...ids][0] : null;
    })();

    const envoyerLienPromotion = useCallback(async () => {
        if (!configurationVisee) return;
        setEnCoursEnvoi(true);
        setErreur(null);
        try {
            const r = await stageAPI.preparerLienPromotion(departementId, configurationVisee);
            origineEnvoi.current = { promotion: configurationVisee };
            setEnvoi(r.data);
            // La liste change de statut : on la relit pour que « lien préparé »
            // apparaisse sans que l'utilisateur ait à rafraîchir.
            recharger();
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error
                || "La préparation du message a échoué");
        } finally {
            setEnCoursEnvoi(false);
        }
    }, [configurationVisee, departementId, recharger]);

    /**
     * L'envoi d'UNE ligne, quel que soit son type (étape ① ou ③).
     *
     * ⚠️ UN SEUL CHEMIN pour tous les envois de ligne. Deux fonctions
     * quasi-identiques auraient divergé sur ce qui compte le plus ici : la
     * relecture du tableau après l'envoi, et la remontée du message d'erreur du
     * serveur — celui qui dit « cet étudiant n'a pas d'adresse » ou « l'adresse
     * publique n'est pas réglée ». Une erreur avalée sur l'une des deux se
     * serait vue le jour où ça compte, pas avant.
     */
    /**
     * Un acte lancé depuis le TABLEAU — exclusion, réintégration.
     *
     * ⚠️ Même chemin que les envois : l'erreur du serveur remonte à l'écran, et
     * le tableau se relit. Un dossier exclu change de pastille, de badge et
     * d'indicateur ; sans relecture, l'assistante verrait l'état d'avant.
     */
    const agirSurLigne = useCallback(async (faire) => {
        setEnCoursEnvoi(true);
        setErreur(null);
        try {
            await faire();
            recharger();
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error
                || "L'action n'a pas abouti");
        } finally {
            setEnCoursEnvoi(false);
        }
    }, [recharger]);

    /**
     * Lot 3.5a — choisir le tuteur depuis la ligne (maquette #tuteurDropdown). La liste se place sous le
     * bouton en position FIXE : dans le tableau qui défile, elle serait coupée. Les enseignants (et leur
     * charge) sont relus à chaque ouverture : la charge change dès qu'une affectation est faite ailleurs.
     */
    const [choixTuteur, setChoixTuteur] = useState(null);          // { d, top, left }
    const [enseignantsTuteurs, setEnseignantsTuteurs] = useState(null);
    const [enCoursTuteur, setEnCoursTuteur] = useState(false);
    const ouvrirChoixTuteur = (d, ev) => {
        const r = ev.currentTarget.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.right - 300, window.innerWidth - 310));
        const top = r.bottom + 360 > window.innerHeight ? Math.max(8, r.top - 360) : r.bottom + 4;
        setChoixTuteur({ d, top, left });
        setEnseignantsTuteurs(null);
        stageAPI.getTuteurs(departementId, anneeId)
            .then((x) => setEnseignantsTuteurs(x.data.enseignants || []))
            .catch((e) => { setChoixTuteur(null); setErreur(e.response?.data?.error || 'Lecture des enseignants impossible'); });
    };
    useEffect(() => {
        if (!choixTuteur) return undefined;
        const dehors = (e) => { if (!e.target.closest('.stg-tuteur-dd')) setChoixTuteur(null); };
        const echap = (e) => { if (e.key === 'Escape') setChoixTuteur(null); };
        document.addEventListener('mousedown', dehors);
        document.addEventListener('keydown', echap);
        return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
    }, [choixTuteur]);
    const affecterTuteurLigne = async (d, tuteurId) => {
        setEnCoursTuteur(true);
        try {
            await stageAPI.affecterTuteur(d.id, departementId, tuteurId);
            setChoixTuteur(null);
            recharger();
            setVersionPanneau((v) => v + 1);
        } catch (e) {
            setChoixTuteur(null);
            setErreur(e.response?.data?.detail || e.response?.data?.error || "L'affectation n'a pas abouti");
        } finally { setEnCoursTuteur(false); }
    };

    const envoyerSurLaLigne = useCallback(async (dossierId, typeMail, relanceDeLaLigne) => {
        const appel = ENVOIS_ARMES[typeMail];
        if (!appel) return;
        setEnCoursEnvoi(true);
        setErreur(null);
        try {
            const r = await appel(stageAPI, dossierId, departementId);
            /**
             * ⚠️ `relance` VIENT DE L'ÉCRAN, le destinataire vient du SERVEUR.
             *
             * Le serveur sait à QUI il écrit (le modèle de courriel le dit) ;
             * il ne sait pas si c'est un premier envoi ou un rappel — cela se
             * lit sur l'état de la ligne, ici. La modale a besoin des deux :
             * « Relancer le maître de stage » sur un premier envoi serait un
             * contresens, et c'est exactement ce qu'elle affichait.
             */
            origineEnvoi.current = { typeMail, dossierId };
            setEnvoi({ ...r.data, relance: relanceDeLaLigne === true });
            recharger();
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error
                || "La préparation du message a échoué");
        } finally {
            setEnCoursEnvoi(false);
        }
    }, [departementId, recharger]);

    if (!departementId) {
        return (
            <div className="stg-encart stg-encart-alerte">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>Aucun département sélectionné : le tableau de bord ne peut pas être lu.</span>
            </div>
        );
    }

    const dossiers = donnees?.dossiers || [];
    // Les semestres du menu suivent la formation choisie : un semestre d'une
    // autre formation ne donnerait aucune ligne, sans dire pourquoi.
    const semestresDuFiltre = (donnees?.semestres || []).filter(
        (s) => !filtres.sousNiveauId || String(s.sous_niveau_id) === String(filtres.sousNiveauId)
    );

    return (
        <>
            {/* ── LE PIPELINE DES ONZE INDICATEURS ───────────────────────── */}
            <div className="stg-pipeline-titre">
                <i className="fa-solid fa-diagram-project" /> Processus suivi
                {/* E179 (Bertrand, 22/10) : les indicateurs comptent ce que les CASES cochent ; la ligne dit le périmètre
                    et ce qui n'est pas compté (rien n'est caché : cocher la case l'ajoute). */}
                {(() => {
                    const ind = (donnees?.indicateurs || [])[0];
                    if (!ind) return null;
                    const n = ind.non_comptes || { total: 0 };
                    const pl = (x, s, p) => `${x} ${x > 1 ? p : s}`;
                    const detail = [n.valide ? pl(n.valide, 'validé', 'validés') : null, n.actif ? pl(n.actif, 'actif', 'actifs') : null,
                        n.abandon ? pl(n.abandon, 'abandon', 'abandons') : null, n.etranger ? `${n.etranger} à l'étranger` : null,
                        n.alternance ? `${n.alternance} passé${n.alternance > 1 ? 's' : ''} en alternance` : null,
                        n.autres ? pl(n.autres, 'autre', 'autres') : null].filter(Boolean).join(', ');
                    return (
                        <span className="stg-pipeline-perimetre">
                            sur {pl(ind.total, 'dossier', 'dossiers')} — cases cochées : {(ind.cochees || []).join(', ') || 'aucune'}
                            {n.total > 0 && <> · non comptés : {detail}</>}
                            {n.valide > 0 && <> · « en cours » et « validés » comptent toujours les validés</>}
                        </span>
                    );
                })()}
            </div>
            <div className="stg-pipeline">
                {(donnees?.indicateurs || []).map((ind, i) => {
                    const ton = TONS[ind.ton] || TONS.orange;
                    // Le séparateur avant les deux derniers : ils comptent des
                    // ÉTATS, pas des étapes du processus (maquette).
                    const separateur = ind.cle === 'en_cours';
                    return (
                        <React.Fragment key={ind.cle}>
                            {separateur && <div className="stg-pipeline-sep" />}
                            {i > 0 && !separateur && <div className="stg-pipeline-fleche">→</div>}
                            <div
                                className="stg-indicateur"
                                style={{
                                    background: ton.fond,
                                    borderColor: ind.bloquant ? '#fde68a' : ton.bord
                                }}
                                title={ind.bloquant
                                    ? "Action de validation en attente — c'est bloquant pour le dossier"
                                    : ind.bilan ? 'Bilan : les cases cochées + les validés, toujours comptés' : undefined}
                            >
                                <div className="stg-indicateur-lib" style={{ color: ton.texte }}>
                                    {/* ⚠️ Emoji, pas Font Awesome : c'est ce qu'écrivent la spec §1
                                        et la maquette. Voir l'écart E46. */}
                                    <span className="stg-indicateur-picto">{ind.picto}</span> {ind.libelle}
                                </div>
                                <div className="stg-indicateur-val" style={{ color: ton.texte }}>
                                    {ind.valeur}<span>/{ind.total}</span>
                                </div>
                                {/* E173 : dans « Validation administrative », celles que le maître a modifiées. */}
                                {ind.modifiees > 0 && (
                                    <div className="stg-indicateur-modifiees" title="Fiches modifiées par le maître : les responsables des stages ont été prévenus">
                                        ✏️ {ind.modifiees} modifiée{ind.modifiees > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* ── LA BARRE DE FILTRES ────────────────────────────────────── */}
            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input
                        placeholder="Rechercher un étudiant…"
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                        aria-label="Rechercher un étudiant"
                    />
                </div>

                {/* L1b : en vue d'ensemble, UN filtre « Processus » ; L3a : par son identifiant (plusieurs formations possibles). */}
                {vueEnsemble && (
                    <select className="input-sm stg-filtre" aria-label="Processus" title="Un processus = ses formations × un semestre"
                            value={filtres.configurationId}
                            onChange={(e) => poser('configurationId', e.target.value)}>
                        <option value="">Tous les processus</option>
                        {processusListe.map((v) => (
                            <option key={v.id} value={v.id}>{v.titre}</option>
                        ))}
                    </select>
                )}
                {/* E169 : dans une vignette, la formation et le semestre sont ceux du processus — pas de filtre. */}
                {!processus && !vueEnsemble && (<>
                <select className="input-sm stg-filtre" value={filtres.sousNiveauId}
                        aria-label="Formation"
                        onChange={(e) => { poser('sousNiveauId', e.target.value); poser('semestreId', ''); }}>
                    <option value="">Toutes les formations</option>
                    {(donnees?.formations || []).map((f) => (
                        <option key={f.id} value={f.id}>{f.nom}</option>
                    ))}
                </select>

                {/* E176 (v90) : le filtre Semestre ne sert qu'aux processus d'avant la v90 — sans semestre, il disparaît. */}
                {semestresDuFiltre.length > 0 && <select className="input-sm stg-filtre" value={filtres.semestreId}
                        aria-label="Semestre"
                        onChange={(e) => poser('semestreId', e.target.value)}>
                    <option value="">Tous les semestres</option>
                    {semestresDuFiltre.map((s) => (
                        <option key={s.id} value={s.id}>{s.semestre}</option>
                    ))}
                </select>}
                </>)}

                <select className="input-sm stg-filtre" value={filtres.statut}
                        aria-label="Statut"
                        onChange={(e) => poser('statut', e.target.value)}>
                    <option value="">Tous les statuts</option>
                    {(donnees?.statuts || []).map((s) => (
                        <option key={s.cle} value={s.cle}>{s.libelle}</option>
                    ))}
                </select>

                <select className="input-sm stg-filtre" value={filtres.tuteurId}
                        aria-label="Tuteur"
                        onChange={(e) => poser('tuteurId', e.target.value)}>
                    <option value="">Tous les tuteurs</option>
                    <option value="aucun">— Sans tuteur —</option>
                    {(donnees?.tuteurs || []).map((t) => (
                        <option key={t.id} value={t.id}>{t.nom} {t.prenom || ''}</option>
                    ))}
                </select>

                {/* ⚠️ Les quatre cases sont INDÉPENDANTES : tout décocher donne une
                    liste vide, et c'est le comportement attendu par la spec. */}
                {(donnees?.familles_visibilite || []).map((f) => (
                    <label key={f.cle} className={'stg-case stg-case-' + f.cle}>
                        <input
                            type="checkbox"
                            checked={visibilite.includes(f.cle)}
                            onChange={() => basculerVisibilite(f.cle)}
                        />
                        {f.libelle}
                    </label>
                ))}
            </div>

            {erreur && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                </div>
            )}

            {/* ── LE TABLEAU ─────────────────────────────────────────────── */}
            <div className="card">
                <div className="card-header">
                    <span>
                        <i className="fa-solid fa-table-list" />{' '}
                        {chargement && !donnees
                            ? 'Lecture…'
                            : `${dossiers.length} dossier${dossiers.length > 1 ? 's' : ''} affiché${dossiers.length > 1 ? 's' : ''}`}
                        {donnees?.annee?.libelle ? ` — ${donnees.annee.libelle}` : ''}
                        {/* ⚠️ Une relecture automatique qu'on ne voit pas ne rassure
                            personne : l'heure dit que l'écran est à jour. */}
                        {luLe && (
                            <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 8, fontSize: 11 }}
                                  title="L'écran se relit tout seul au retour sur l'onglet, et chaque minute.">
                                · à jour à {luLe.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                    </span>
                    {/* ⚠️ L'envoi groupé vise UNE configuration : il n'a de sens
                        que si le filtre en désigne une seule. Avec plusieurs
                        promotions à l'écran, on ne saurait pas laquelle est
                        visée — le bouton le dit plutôt que d'en choisir une. */}
                    {/* L1b : en vue d'ensemble, l'envoi à une promotion se fait depuis la vignette de SON processus. */}
                    {vueEnsemble ? (
                        <span style={{ fontSize: 11, color: 'var(--stg-muted)' }}>
                            <i className="fa-solid fa-circle-info" /> L'envoi à une promotion se fait depuis la vignette de son processus (cliquez sa formation)
                        </span>
                    ) : (
                    <button
                        type="button" className="btn btn-green btn-sm"
                        disabled={!configurationVisee || enCoursEnvoi || !modifiable}
                        title={!modifiable
                            ? "Lecture seule : votre profil ne permet pas de modifier ce module."
                            : configurationVisee
                                ? "Préparer le lien commun pour toute la promotion affichée"
                                : "Filtrez sur UNE formation et UN semestre : l'envoi groupé vise une promotion."}
                        onClick={envoyerLienPromotion}
                    >
                        {enCoursEnvoi
                            ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Préparation…</>
                            : <><i className="fa-solid fa-paper-plane" /> Envoyer le lien à la promotion</>}
                    </button>
                    )}
                </div>

                {/* v75 : les dossiers dont l'étudiant est passé en alternance — dit en tête, filtrable en un clic. */}
                {(filtres.aExclure || dossiers.some((x) => x.passe_alternance)) && (
                    <div className="stg-encart stg-encart-alerte stg-encart-alternance">
                        <i className="fa-solid fa-arrows-rotate" />
                        <span>
                            {filtres.aExclure
                                ? <><b>{dossiers.length} dossier{dossiers.length > 1 ? 's' : ''}</b> dont l'étudiant est passé en alternance (apprentissage ou FC) : excluez-les du suivi, motif « Passé en alternance » (pastille de la colonne Suivi).</>
                                : <><b>{dossiers.filter((x) => x.passe_alternance).length} dossier{dossiers.filter((x) => x.passe_alternance).length > 1 ? 's' : ''}</b> dont l'étudiant est passé en alternance (apprentissage ou FC) depuis la création de son dossier : à exclure du suivi des stages.</>}
                        </span>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => poser('aExclure', !filtres.aExclure)}>
                            {filtres.aExclure ? 'Tout afficher' : 'Voir seulement ces dossiers'}
                        </button>
                    </div>
                )}
                <div className="stg-table-defile">
                    <table>
                        <thead>
                            <tr>
                                <th>Étudiant</th>
                                <th style={{ width: 50, textAlign: 'center' }} title="Suivi actif / exclusion">Suivi</th>
                                <th>N°</th>
                                <th>Formation</th>
                                {/* ⚠️ L'EMOJI, pas `fa-building-columns` : la maquette écrit
                                    littéralement 🏛️ dans cet en-tête (écart E46). */}
                                <th style={{ width: 36, textAlign: 'center' }}
                                    title="Convention université — cochée par le maître à l'étape ⑤">
                                    🏛️
                                </th>
                                <th>Entreprise</th>
                                {/* ⚠️ « Progression » NU, comme la maquette. La légende des dix
                                    étapes vivait ici depuis le lot 3.1, sur la foi du test S15 —
                                    Bertrand a tranché le 2026-09-16 : c'est la SPEC qui avait
                                    tort, et S15 est corrigé. La lecture des dix carrés repose
                                    désormais entièrement sur la bulle au survol (écart E43). */}
                                <th>Progression</th>
                                <th>Action suivante</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!chargement && dossiers.length === 0 && (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', color: '#64748b', padding: '24px 14px' }}>
                                        {visibilite.length === 0
                                            ? 'Aucune case de visibilité n\'est cochée — cochez « Actifs » pour revoir les dossiers.'
                                            : 'Aucun dossier ne correspond à ces filtres.'}
                                    </td>
                                </tr>
                            )}

                            {dossiers.map((d) => (
                                <tr
                                    key={d.id}
                                    className={'stg-ligne' + (d.retard ? ' en-retard' : '')}
                                    onClick={() => { setDossierOuvert(d.id); setAncre(null); }}
                                    title="Cliquer pour ouvrir le dossier"
                                >
                                    <td>
                                        {/**
                                          * ⚠️ LA PHOTO PASSE PAR `PhotoEtudiant`, PAS PAR UN `<img src>`.
                                          *
                                          * `photo_url` désigne une route de l'API qui EXIGE le jeton ; un
                                          * `<img>` écrit tel quel émet la requête lui-même, sans en-tête
                                          * `Authorization`, et laisse des vignettes vides SANS erreur —
                                          * mesuré au lot 1.5 d'Étudiants. Le composant la demande par
                                          * axios et la garde en cache AU NIVEAU DU MODULE : venir de la
                                          * liste des étudiants, c'est arriver ici avec les visages déjà
                                          * chargés, zéro requête.
                                          *
                                          * ⚠️ Même vocabulaire visuel que le module Étudiants — mêmes
                                          * `teinteAvatar` et `initiales`, même rond de 28 px : deux écrans
                                          * qui montrent les mêmes personnes ne doivent pas les dessiner
                                          * différemment.
                                          *
                                          * Mesuré le 2026-09-16 : 58 des 59 dossiers ont une photo,
                                          * 182 ko au total, 3,2 ko en moyenne. Le 59ᵉ porte ses initiales,
                                          * jamais un trou.
                                          */}
                                        <div className="stg-identite">
                                            <PhotoEtudiant
                                                chemin={d.photo_url}
                                                className="stg-avatar stg-avatar-photo"
                                                alt=""
                                                secours={(
                                                    <div className="stg-avatar"
                                                         style={{ background: teinteAvatar(d.nom, d.prenom) }}>
                                                        {initiales(d.nom, d.prenom)}
                                                    </div>
                                                )}
                                            />
                                            <div>
                                                <strong>{d.nom}</strong> {d.prenom}
                                        {/* L3b (E172) : un apprenti suivi dans ce processus (ni convention ni documents de retour). */}
                                        {d.parcours === 'apprenti' && (
                                            <> <span className="stg-pastille-apprenti" title={d.apprenti_en_fi
                                                ? "Inscrit en formation initiale avec le régime apprenti : à corriger dans le module Étudiants (il suit le parcours apprenti)"
                                                : "Apprenti : suivi ici, sans convention ni documents de retour ; son tuteur vient du registre"}>
                                                {d.apprenti_en_fi && <i className="fa-solid fa-triangle-exclamation" />} apprenti</span></>
                                        )}
                                        {d.retard && (
                                            <div className="stg-retard">
                                                <i className="fa-solid fa-clock" /> En retard — {d.retard.jours} j
                                            </div>
                                        )}
                                        {/* E99 : un RAPPEL à J − délai, pas une contrainte — la
                                            grille peut partir avant, dès les documents validés. */}
                                        {/* v75 : l'étudiant n'est plus en formation initiale cette année (apprenti / FC). */}
                                        {d.passe_alternance && (
                                            <div className="stg-retard stg-alternance" title="Son inscription de l'année n'est plus en formation initiale (apprentissage ou formation continue) : ce dossier n'a plus lieu d'être — Exclure du suivi, motif « Passé en alternance »">
                                                <i className="fa-solid fa-arrows-rotate" /> Passé en alternance — à exclure
                                            </div>
                                        )}
                                        {d.alerte_grille && (
                                            <div className="stg-retard" title={`Délai d'envoi atteint depuis le ${dateFr(d.alerte_grille.a_partir_du)}`}>
                                                <i className="fa-solid fa-star" /> Grille à envoyer — fin le {dateFr(d.alerte_grille.fin)}
                                            </div>
                                        )}
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        {/* ⚠️ LA PASTILLE FAIT CE QUE FAIT « Modifier » DANS LE
                                            DOSSIER — demandé par Bertrand le 2026-09-25.
                                            Elle ouvre la MÊME modale, avec le même vocabulaire :
                                            deux écrans pour le même geste finiraient par ne plus
                                            dire la même chose. Un dossier validé garde sa pastille
                                            inactive : il est archivé, l'exclusion n'a plus d'objet. */}
                                        <button
                                            type="button"
                                            className={'action-btn stg-suivi stg-suivi-' + (d.motif_exclusion || 'actif')}
                                            disabled={d.statut === 'valide' || !modifiable}
                                            onClick={() => setExclusionLigne(d)}
                                            title={d.statut === 'valide'
                                                ? 'Stage validé — archivage automatique'
                                                : !modifiable
                                                    ? "Lecture seule : votre profil ne permet pas de modifier ce module."
                                                    : d.motif_exclusion
                                                        ? `Exclu — ${(LIBELLE_MOTIF[d.motif_exclusion] || d.motif_exclusion).toLowerCase()}. Cliquer pour réintégrer.`
                                                        : 'Suivi actif. Cliquer pour exclure du suivi.'}
                                        >
                                            <i className={'fa-solid ' + (d.motif_exclusion ? 'fa-user-slash' : 'fa-user-check')} />
                                        </button>
                                    </td>
                                    <td style={{ fontSize: 12, color: '#64748b' }}>{d.code_etudiant || '—'}</td>
                                    {/* L1b : en vue d'ensemble, la formation ouvre la vignette de SON processus. */}
                                    {vueEnsemble && onOuvrirProcessus ? (
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <button type="button" className="stg-lien-processus" title="Ouvrir son processus"
                                                    onClick={() => onOuvrirProcessus(d.configuration_id)}>
                                                {d.formation_nom} <span>{d.semestre}</span>
                                            </button>
                                        </td>
                                    ) : (
                                    <td>{d.formation_nom} <span style={{ color: '#94a3b8' }}>{d.semestre}</span></td>
                                    )}
                                    {/* ── 🏛️ LA CONVENTION UNIVERSITÉ ──────────────────
                                        ⚠️ TROIS ÉTATS, et ils ne se ressemblent pas :
                                        cochée (🏛️), répondue sans cocher (« non »), et
                                        **pas encore répondue** (—). Écrire « non » avant
                                        que le maître ait répondu affirmerait ce que
                                        personne ne sait (S47). */}
                                    <td style={{ textAlign: 'center', fontSize: 14 }}
                                        title={d.convention_universite === true
                                            ? "Convention université cochée par le maître (étape ⑤)"
                                            : d.convention_universite === false
                                                ? "Le maître n'a pas retenu la convention de l'université"
                                                : "Convention université — non encore connue (étape ⑤ non atteinte)"}>
                                        {d.convention_universite === true ? '🏛️'
                                            : d.convention_universite === false
                                                ? <span style={{ color: '#94a3b8', fontSize: 12 }}>non</span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                    </td>
                                    <td style={d.entreprise_nom ? undefined : { color: '#94a3b8', fontStyle: 'italic' }}
                                        title={d.entreprise_nom
                                            ? d.entreprise_nom
                                            : "L'étudiant n'a pas encore soumis sa fiche de liaison"}>
                                        {d.entreprise_nom || '—'}
                                    </td>
                                    <td>
                                        <StageBarreProgression
                                            segments={d.progression}
                                            faites={d.progression_faites}
                                            total={d.progression_total}
                                            compact
                                        />
                                    </td>
                                    <td>
                                        {/* E182 : une correction demandée (retour en arrière) — badge rose, la ligne est remontée. */}
                                        <span className={'badge ' + (d.action_suivante?.bloquant ? 'badge-bloquant'
                                            : d.action_suivante?.retour ? 'badge-retour'
                                            : d.action_suivante?.fini ? 'badge-ok' : 'badge-wait')}>
                                            {d.action_suivante?.picto} {d.action_suivante?.libelle}
                                        </span>
                                        {/* E173 : la fiche que le maître a modifiée (alerte aux responsables). */}
                                        {d.fiche_modifiee && d.statut === 'fiche_maitre_validee' && (
                                            <span className="stg-pastille-modifiee" title="Le maître a modifié la saisie de l'étudiant — voir ⑤ dans le dossier">✏️ modifiée</span>
                                        )}
                                    </td>
                                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                                        {/* Lot 3.5a : le tuteur se choisit depuis la ligne (maquette #tuteurDropdown). */}
                                        {d.visite_active && (
                                            <button type="button" className={'action-btn' + (d.tuteur ? ' stg-action-tuteur' : '')}
                                                    disabled={!modifiable || !!d.tuteur_verrou}
                                                    onClick={(ev) => ouvrirChoixTuteur(d, ev)}
                                                    title={!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.'
                                                        : d.tuteur_verrou || (d.tuteur ? `Modifier le tuteur (${d.tuteur.nom} ${d.tuteur.prenom || ''})` : 'Affecter un tuteur')}>
                                                <i className="fa-solid fa-chalkboard-user" />
                                            </button>
                                        )}
                                        {/* ⚠️ Deux envois sont armés : le lien de l'étudiant (① lot 3.2)
                                            et la fiche au maître de stage (③ lot 3.2 bis). Les autres
                                            restent grisés AVEC LE NUMÉRO DE LEUR LOT dans l'infobulle —
                                            un bouton mort sans explication se lit comme une panne. */}
                                        {/* ⚠️ L'ICÔNE ⊙ DE LA DÉCISION (test S20, lot 3.3).
                                            Sur un dossier qui attend l'arbitrage du département,
                                            l'action n'est PAS un envoi : c'est une décision. Le
                                            clic ouvre le panneau **positionné sur le bloc**, pour
                                            éviter de faire défiler douze sections à la recherche
                                            de ce qu'on est venu faire. */}
                                        {/* E173 : la décision ③ (responsable, fiche de l'étudiant) s'ouvre de la même façon. */}
                                        {(d.statut === 'fiche_maitre_validee'
                                          || (d.statut === 'fiche_etudiant_saisie' && d.action_suivante?.attend === 'responsable')) ? (
                                            <button
                                                type="button" className="action-btn stg-action-decision"
                                                disabled={!modifiable}
                                                onClick={() => { setDossierOuvert(d.id); setAncre('decision'); }}
                                                title={!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.'
                                                    : d.statut === 'fiche_etudiant_saisie'
                                                        ? `Valider la fiche de ${d.prenom} ${d.nom} (responsable) : valider, renvoyer à l'étudiant, ou refuser`
                                                        : `Validation administrative de ${d.prenom} ${d.nom} : valider, renvoyer au maître, ou refuser`}
                                            >
                                                <i className="fa-solid fa-circle-dot" />
                                            </button>
                                        ) : (ENVOIS_ARMES[d.action_suivante?.mail] && modifiable) ? (
                                            <button
                                                type="button" className="action-btn"
                                                disabled={enCoursEnvoi}
                                                onClick={() => envoyerSurLaLigne(
                                                    d.id, d.action_suivante.mail, d.action_suivante.relance)}
                                                title={infobulleEnvoi(d)}
                                            >
                                                {/* ⚠️ L'avion de papier pour l'envoi au maître, comme la
                                                    maquette ; la flèche de relance dès qu'on attend
                                                    quelqu'un ; l'enveloppe pour le premier lien. */}
                                                <i className={'fa-solid ' + (
                                                    d.action_suivante.relance ? 'fa-rotate-right'
                                                    : d.action_suivante.mail === 'maitre_fiche' ? 'fa-paper-plane'
                                                    // L'étoile de la maquette pour la grille d'évaluation.
                                                    : d.action_suivante.mail === 'grille_eval' ? 'fa-star'
                                                    : 'fa-envelope')} />
                                            </button>
                                        ) : (
                                            <button
                                                type="button" className="action-btn" disabled
                                                title={d.action_suivante?.mail
                                                    ? `« ${d.action_suivante.libelle} » — cet envoi arrive au lot ${LOT_DU_MAIL[d.action_suivante.mail] || '3.4'}.`
                                                    : "Cette action n'est pas un envoi : elle se traite dans le dossier."}
                                            >
                                                <i className={'fa-solid ' + (d.action_suivante?.relance ? 'fa-rotate-right' : 'fa-envelope')} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Lot 3.5a : choisir le tuteur depuis la ligne — position fixe, hors du tableau qui défile. */}
            {choixTuteur && (
                <div className="stg-tuteur-dd" style={{ top: choixTuteur.top, left: choixTuteur.left }} role="dialog" aria-label="Choisir le tuteur">
                    <div className="stg-tuteur-dd-entete">
                        <small>{choixTuteur.d.nom} {choixTuteur.d.prenom} — tuteur actuel</small>
                        <strong>{choixTuteur.d.tuteur ? `${choixTuteur.d.tuteur.nom} ${choixTuteur.d.tuteur.prenom || ''}` : 'Aucun'}</strong>
                    </div>
                    {!enseignantsTuteurs
                        ? <div className="stg-panneau-attente" style={{ padding: 10 }}><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des enseignants…</div>
                        : <StageChoixTuteur enseignants={enseignantsTuteurs} actuelId={choixTuteur.d.tuteur?.id || null} enCours={enCoursTuteur}
                                            onAffecter={(id) => affecterTuteurLigne(choixTuteur.d, id)} />}
                </div>
            )}

            {/* Lot 3.S-b2 : après un envoi par le serveur, le tableau ET le panneau se relisent. */}
            {envoi && <StageModaleEnvoi envoi={envoi} departementId={departementId} onFermer={() => setEnvoi(null)}
                                        onTermine={() => { recharger(); setVersionPanneau((v) => v + 1); }}
                                        rePreparer={async () => {
                                            // ⚠️ Jamais la route d'origine d'un message né d'une DÉCISION : elle la rejouerait.
                                            const o = origineEnvoi.current;
                                            if (envoi.type === 'promotion') return (await stageAPI.preparerLienPromotion(departementId, envoi.configuration_id)).data;
                                            if (envoi.type === 'tuteurs' && o?.typeMail === 'tuteur_affectation') return (await ENVOIS_ARMES.tuteur_affectation(stageAPI, o.dossierId, departementId)).data;
                                            if (envoi.dossier_id && envoi.type) return (await stageAPI.brouillonEnvoiDossier(envoi.dossier_id, departementId, envoi.type)).data;
                                            return null;
                                        }} />}

            {/* ⚠️ La forme attendue est celle du PANNEAU (`etudiant.nom`), alors
                que la ligne du tableau porte `nom` à plat. On la compose ici,
                plutôt que d'apprendre deux formes à la modale : c'est l'appelant
                qui s'adapte, pas le composant partagé. */}
            {exclusionLigne && (
                <StageModaleExclusion
                    dossier={{
                        id: exclusionLigne.id,
                        motif_exclusion: exclusionLigne.motif_exclusion,
                        commentaire_exclusion: exclusionLigne.commentaire_exclusion,
                        etudiant: { nom: exclusionLigne.nom, prenom: exclusionLigne.prenom }
                    }}
                    enCours={enCoursEnvoi}
                    onFermer={() => setExclusionLigne(null)}
                    onExclure={(motif, commentaire) => {
                        const id = exclusionLigne.id;
                        setExclusionLigne(null);
                        agirSurLigne(() => stageAPI.exclureDossier(id, departementId, motif, commentaire));
                    }}
                    onReintegrer={() => {
                        const id = exclusionLigne.id;
                        setExclusionLigne(null);
                        agirSurLigne(() => stageAPI.reintegrerDossier(id, departementId));
                    }}
                />
            )}

            {dossierOuvert && (
                <StagePanneauDossier
                    onVoirEvaluation={onVoirEvaluation}
                    dossierId={dossierOuvert}
                    departementId={departementId}
                    anneeId={anneeId}
                    user={user}
                    ancre={ancre}
                    onFermer={() => { setDossierOuvert(null); setAncre(null); }}
                    /* ⚠️ Une décision prise DANS le panneau change la ligne DERRIÈRE
                       lui : badge, barre de progression, indicateurs du pipeline.
                       Sans cette relecture, l'assistante refermerait le panneau sur
                       un tableau qui affiche encore l'état d'avant. */
                    modifiable={modifiable}
                    onChange={recharger}
                    /* Les CAS ① et ② préparent un message : il s'affiche dans la
                       même modale que les autres envois — un seul vocabulaire. */
                    onMessage={(message) => { if (message && (message.mailto || message.mode === 'smtp')) { origineEnvoi.current = null; setEnvoi(message); } }}
                    version={versionPanneau}
                />
            )}
        </>
    );
}

export default StageDashboard;
